import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GlobalSearchModal } from './GlobalSearchModal';

interface GlobalSearchTrigger {
  open: (initialQuery?: string) => void;
}

const GlobalSearchTriggerContext = createContext<GlobalSearchTrigger>({ open: () => undefined });

/** Lets any descendant (the header button) open the palette. */
export function useGlobalSearchTrigger(): GlobalSearchTrigger {
  return useContext(GlobalSearchTriggerContext);
}

export const GlobalSearchProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState<string | undefined>(undefined);
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  // Mirrors `open` so the document listener can read it without re-subscribing
  // on every toggle.
  const isOpen = useRef(false);

  useEffect(() => {
    isOpen.current = open;
  }, [open]);

  const openPalette = useCallback((initialQuery?: string) => {
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    setSeed(initialQuery);
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    // Return focus where the user left it, per the dialog pattern.
    restoreFocusTo.current?.focus?.();
    restoreFocusTo.current = null;
  }, []);

  useEffect(() => {
    // Document-level on purpose: ⌘K must work while focus is in a text field,
    // so this cannot be a per-input handler. preventDefault stops Firefox's
    // Ctrl+K search bar and Chrome's ⌘K binding from stealing it.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key?.toLowerCase() !== 'k') return;
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      // Route through the same open/close helpers the rest of the component uses,
      // so focus restoration happens on every close path — not just Escape.
      if (isOpen.current) closePalette();
      else openPalette();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closePalette, openPalette]);

  return (
    <GlobalSearchTriggerContext.Provider value={{ open: openPalette }}>
      {children}
      <GlobalSearchModal open={open} initialQuery={seed} onClose={closePalette} />
    </GlobalSearchTriggerContext.Provider>
  );
};
